import { SHORTENED_ERROR_MESSAGES } from '@/enums';
import { PRIVATE_ROUTES } from '@/routes/routes';
import { CustomError } from '@/utils';
import { notification } from 'antd';
import { getI18n } from 'react-i18next';
import { generatePath } from 'react-router-dom';

// Kept free of Order form/schema imports so it can be used from
// src/queries/order.query.ts without creating an import cycle.

const INVOICE_PRODUCT_PRICING_NOT_FOUND_NOTIFICATION_KEY =
  'INVOICE_PRODUCT_PRICING_NOT_FOUND';

const getErrorResponseData = (error: unknown) =>
  (error as CustomError | undefined)?.response?.data;

/**
 * Backend 400 raised by PUT /orders/:id and POST /invoices/check-eligibility
 * when a product (or all of its components) has no invoice price.
 * `details.name` is the product name; `details.productId` may be absent.
 */
export const isInvoicePricingNotFoundError = (error: unknown): boolean =>
  getErrorResponseData(error)?.errorCode ===
  SHORTENED_ERROR_MESSAGES.INVOICE_PRODUCT_PRICING_NOT_FOUND;

export const notifyInvoicePricingNotFound = (error: unknown) => {
  const i18n = getI18n();
  const details = getErrorResponseData(error)?.details ?? {};
  const productName =
    typeof details.name === 'string' && details.name ? details.name : '';
  const productId =
    typeof details.productId === 'string' && details.productId
      ? details.productId
      : undefined;

  const productPageUrl = productId
    ? `${PRIVATE_ROUTES.PRODUCT.ROOT}/${generatePath(
        PRIVATE_ROUTES.PRODUCT.EDIT,
        { id: productId },
      )}`
    : undefined;

  notification.error({
    key: INVOICE_PRODUCT_PRICING_NOT_FOUND_NOTIFICATION_KEY,
    message: i18n.t(
      `error.${SHORTENED_ERROR_MESSAGES.INVOICE_PRODUCT_PRICING_NOT_FOUND}`,
      { name: productName || i18n.t('order.invoicePricingUnknownProduct') },
    ),
    description: productPageUrl ? (
      <a href={productPageUrl} target="_blank" rel="noopener noreferrer">
        {i18n.t('product.goToProductPage')}
      </a>
    ) : undefined,
  });
};
